import { computed, customRef, Ref, ref } from "vue";

import { diffTwoObjects } from "@/utils";

export const clone = <T>(value: T): T => {
  if (value === undefined) {
    return value;
  }

  return JSON.parse(JSON.stringify(value)) as T;
};

export const writable = <T>(upstream: () => T) => {
  // It's a bit weird, but this essentially guarantees that the ref gets reset
  // immediately as expected. This also gets around the double-calling that
  // using `watch` would cause.
  const computable = computed(() => ref(upstream()));

  // A customRef here because we already have `computed` and `ref` doing their
  // jobs above, just need the thing that'd make Vue recognize it's one of them.
  return customRef<T>(() => ({
    get() {
      return computable.value.value;
    },
    set(next) {
      computable.value.value = next;
    },
  }));
};

export const useDirty = <Record, DirtyKeyOfRecord extends keyof Record>(
  backingObject: Readonly<Ref<Record | undefined>>,
  keys: DirtyKeyOfRecord[]
) => {
  const object = writable(() => clone(backingObject.value)) as Ref<
    Pick<Record, DirtyKeyOfRecord>
  >;

  const hasUnsavedChanges = computed(() => {
    if (backingObject.value === undefined) {
      return false;
    }

    return (
      Object.keys(
        diffTwoObjects<Pick<Record, DirtyKeyOfRecord>>(
          backingObject.value,
          object.value,
          keys
        )
      ).length > 0
    );
  });

  return {
    object,
    hasUnsavedChanges,
    resetChanges: () => {
      object.value = clone(backingObject.value) as Record;
    },
  };
};
